/* -----------------Globals--------------- */
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Platform, type TextInput as RNTextInput } from "react-native";

/* -----------------UI--------------- */
import { Caption, TextInput } from "react-native-paper";

/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "../../useAppTheme";

/* -----------------Types--------------- */
import type { FeedbackParams } from "./types";

import { Field, Form } from "houseform";
/* -----------------Child components--------------- */
import Box from "../Box";

/* -----------------Libraries--------------- */
import { z } from "zod";

const MAX_FEEDBACK_LENGTH = 1000;

type FeedbackFormRef = {
	submit: () => void;
};

type FeedbackFormProps = {
	feedbackDialogParams: FeedbackParams;
	feedbackReasonList?: {
		title: string;
		description: string;
		icon: string;
		value: string;
	}[];
	onSubmit: (feedback: string, email: string) => void;
	onEmailValidationChange: (isValid: boolean) => void;
	onFeedbackValidationChange: (isValid: boolean) => void;
	onEmailTextChange: (text: string) => void;
	onFeedbackTextChange: (text: string) => void;
	feedbackLimitError: string;
	feedbackPlaceholder: string;
};

export const FeedbackForm = forwardRef<FeedbackFormRef, FeedbackFormProps>(
	(
		{
			feedbackDialogParams,
			feedbackReasonList,
			onSubmit,
			onEmailValidationChange,
			onFeedbackValidationChange,
			onEmailTextChange,
			onFeedbackTextChange,
			feedbackLimitError,
			feedbackPlaceholder,
		},
		ref,
	) => {
		const submitRef = useRef<(() => void) | null>(null);
		const feedbackInputRef = useRef<RNTextInput>(null);
		const theme = useAppTheme();

		useEffect(() => {
			const timer = setTimeout(() => {
				feedbackInputRef.current?.focus();
			}, 300);
			return () => clearTimeout(timer);
		}, []);

		useImperativeHandle(ref, () => ({
			submit: () => {
				if (submitRef.current) {
					submitRef.current();
				}
			},
		}));

		return (
			<Form
				onSubmit={(values) => {
					onSubmit(values.feedback, values.email);
				}}
			>
				{({ submit }) => {
					submitRef.current = submit;

					return (
						<Box gap={24} width="100%">
							{!feedbackDialogParams?.email && (
								<Field
									name="email"
									onChangeValidate={z
										.string()
										.email("Please enter a valid email address")}
								>
									{({ value, setValue, onBlur, errors, isValid }) => {
										onEmailValidationChange(isValid);

										return (
											<Box>
												<TextInput
													label="Email"
													mode="outlined"
													placeholder="e.g., dave@gmail.com"
													onChangeText={(text) => {
														setValue(text);
														onEmailTextChange(text);
													}}
													value={value}
													onBlur={onBlur}
													autoCapitalize="none"
													keyboardType="email-address"
													onSubmitEditing={submit}
													error={errors.length > 0}
												/>
											</Box>
										);
									}}
								</Field>
							)}
							<Field
								name="feedback"
								onBlurValidate={z
									.string()
									.min(feedbackReasonList ? 0 : 10, feedbackLimitError)}
								onChangeValidate={z.string()}
							>
								{({ value, setValue, onBlur, errors, isTouched }) => {
									const minLength =
										feedbackReasonList && feedbackReasonList.length > 0
											? 0
											: 10;
									const meetsMinLength = value.length >= minLength;
									onFeedbackValidationChange(meetsMinLength);

									return (
										<Box>
											<TextInput
												ref={feedbackInputRef}
												multiline
												mode="outlined"
												autoFocus
												maxLength={MAX_FEEDBACK_LENGTH}
												error={
													isTouched && errors.length > 0 && value.length > 0
												}
												style={{
													backgroundColor: theme.colors.surface,
													paddingTop: Platform.OS === "android" ? 16 : "auto",
													height: 200,
												}}
												onBlur={onBlur}
												placeholder={feedbackPlaceholder}
												value={value}
												onChangeText={(text) => {
													setValue(text);
													onFeedbackTextChange(text);
												}}
												onSubmitEditing={submit}
											/>

											{value.length >= MAX_FEEDBACK_LENGTH * 0.9 && (
												<Caption
													style={{
														textAlign: "right",
													}}
												>
													{value.length}/{MAX_FEEDBACK_LENGTH}
												</Caption>
											)}
										</Box>
									);
								}}
							</Field>
						</Box>
					);
				}}
			</Form>
		);
	},
);

FeedbackForm.displayName = "FeedbackForm";
